import { UntypedFormArray, ValidatorFn } from "@angular/forms";

export class PopulationCriteria {
  id: number;
  customVariableId: number | null;
  translationKey: string;
  name: string;
  active: boolean;
  showInput: boolean = false;
  order : number;
  configuration : PopulationCriteriaConfiguration;
  category : string;
  description : string;
  optionsQty? : number = 0;
  extraConfiguration : ExtraConfiguration[];
}

export enum PopulationCriteriaCategory{
  CALCULATED = "CALCULATED",
  INPUT = "INPUT",
  TRIGGER = "TRIGGER",
  CLIENT_CUSTOM_VARIABLES = "CLIENT_CUSTOM_VARIABLES",
  PUNISHED_PORTFOLIO = "PUNISHED_PORTFOLIO"
}

export const PopulationCriteriaIdentifier = {
  CUSTOM_VARIABLE : 0,
  PROPENSITY_TO_PAY : 1,
  TOTAL_BALANCE : 2,
  PREFERRED_COMMUNICATION_CHANNEL : 3,
  PREFERRED_PAYMENT_CHANNEL : 4,
  AGE : 5,
  GENDER : 6,
  CAMPAIGN_ID : 7,
  PRODUCT : 8,
  NEW_CLIENT : 9,
  VAR1 : 10,
  VAR2 : 11,
  VAR3 : 12,
  VAR4 : 13,
  VAR5 : 14,
  VAR6 : 15,
  VAR7 : 16,
  VAR8 : 17,
  VAR9 : 18,
  VAR10 : 19,
  UNFULFILLED_COMMITMENT : 20,
  OVERDUE_BALANCE : 21,
  ARREARS_PERIOD : 22,
  ADVISER_ID : 23,
  SUPERVISOR_ID : 24,
  CUSTOMERS_WITH_UP_TO_DATE_PAYMENTS_LAST_X_QUOTAS : 25,
  PARENT_CLIENT_ID_ASSOCIATED : 26,
  CLIENT_ANTIQUITY : 27,
  NON_PAYMENT_PROBLEM : 28,
  CONTACT_PROBLEM : 29,
  PAYMENT_ISSUES : 30,
  PORTFOLIO_TYPE : 31,
  CONTACT_RESPONSE_ACTION : 32,
  ARREARS_QTY_LAST_X_MONTHS : 33,
  CHAMPION_CHALLENGER : 34,
  PENDING_PAYMENT_ORDERS_COUNT : 35,
  PAYMENT_TYPE : 36,
  CLAIM: 37,
  STATUS_PURCHASE_ORDER: 38,
  FREQUENCY_PURCHASE_ORDER: 39,
  NEXT_AMOUNT: 41,
  NO_CONTACTABLE: 43,
  LAST_EMAIL_STATUS_CUSTOMER: 44,
  LAST_EMAIL_STATUS_REFERENT: 45,
  LAST_EMAIL_STATUS_GUARANTOR: 46,
  LAST_WHATSAPP_STATUS_CUSTOMER: 47,
  LAST_WHATSAPP_STATUS_REFERENT: 48,
  LAST_WHATSAPP_STATUS_GUARANTOR: 49,
  SUBSCRIPTION_STATUS: 50,
  PUNISHED_AMOUNT: 51,
  PUNISHED_TOTAL_AMOUNT: 52,
  PUNISHED_ASSOCIATED_CODE: 53,
  PUNISHED_OFFER_AMOUNT: 54,
  TRADE_NAME: 55,
  CLIENT_SITUATION: 56,
  LAST_PAYMENT_STATUS_INDICATOR: 57,
  EMAIL_OPERATIVE_U30D: 58,
  WHATSAPP_OPERATIVE_U30D: 59,
  CONTACTABILITY_STATUS: 60,
  WEB_FORM_USAGE: 61,
  PREVIOUS_COMMUNICATION_STATUS: 62,
  OVERDUE_PAYMENT_ORDERS_COUNT: 63,
  OVERDUE_BALANCE_MONO_INTERFACE: 64,
}

export enum PopulationCriteriaSelection {
  INPUT_NUMBER = 'INPUT_NUMBER',
  INPUT_INTEGER_NUMBER = 'INPUT_INTEGER_NUMBER',
  INPUT = 'INPUT',
  SELECT_MULTIPLE = 'SELECT_MULTIPLE',
  SELECT_MULTIPLE_SEARCH = 'SELECT_MULTIPLE_SEARCH',
  SELECT = 'SELECT',
  CUSTOM_INPUTS_CONTACTABILITY = 'CUSTOM_INPUTS_CONTACTABILITY',
  CUSTOM_INPUTS_PREVIOUS_COMMUNICATION = 'CUSTOM_INPUTS_PREVIOUS_COMMUNICATION',
  CUSTOM_INPUTS_WEB_FORM_USAGE = 'CUSTOM_INPUTS_WEB_FORM_USAGE',
  EXTRA_DATA_INPUT_NUMBER = 'EXTRA_DATA_INPUT_NUMBER',
  MULTIPLE_INPUTS_NUMBER = 'MULTIPLE_INPUTS_NUMBER',
  CHECKBOX = 'CHECKBOX'
}

export class PopulationCriteriaParam {
  type: string;
  value: string | null;
  includeCondition : string;
  conditionalType? : number;
  secondValue? : string;
  thirdValue? : string;
  fourthValue? : string;
}

export class ExtraConfiguration{
  economicSectorId : number;
  hierarchicalRelation : string;
  translationKey : string;
  description : string;
}
export class VisibilityConfig {
  projectType : string;
  populationTypes : string[];
}

export class PopulationCriteriaConfiguration {
  visibility: VisibilityConfig[];
}

export enum AditionalVariableIdentifier {
  VAR1 = 1,
  VAR2 = 2,
  VAR3 = 3,
  VAR4 = 4,
  VAR5 = 5,
  VAR6 = 6,
  VAR7 = 7,
  VAR8 = 8,
  VAR9 = 9,
  VAR10 = 10,
}

export const PopulationCriteriaNumericParam = {
  GREATER_THAN : 1,
  SMALLER_THAN : 2,
  EQUAL_TO : 3,
  IT_IS_BETWEEN : 4,
  NO_ASSIGNED : 5,
  UNLIKE : 6
}

export const PopulationCriteriaIncludeRefGar = {
  NO : 0,
  YES : 1,
}

export const PopulationCriteriaPeriod = {
  LAST_ONE : 0,
  PREVIOUS : 1,
}

// Estados de comunicación reutilizados por los criterios de estado de email / whatsapp / sms / llamada.
export enum CommunicationStatus {
  SEND = 'S',
  DELIVERED = 'D',
  READ = 'R',
  CLICK = 'C',
  FAILED = 'F',
  SPAM = 'B',
  INACTIVE = 'I',
  VALID = 'V',
  NOT_VALID = 'N',
}

export const PopulationCriteriaTypes = {
  GENERAL_POPULATION_TYPE : 'P',
  CAMPAIGN_POPULATION_TYPE : 'C'
}

export enum IncludeCondition{
  INCLUDE = 'I',
  EXCLUDE = 'E'
}

export enum BasicResponse{
  YES = 'YES',
  NO = 'NO'
}

export type CriterionFillContext = {
  populationCriteriaId: number;
  dataToFill: PopulationCriteriaParam | null;
  indexConfiguration: number;
  params: UntypedFormArray;
  index: number;
};

export type CriterionDef = {
  type : PopulationCriteriaSelection;
  options? : () => { value: string; key: string }[];
  options2?: () => { value: number; key: string }[];
  validators : ValidatorFn | null;
  fill? : (context : CriterionFillContext) => void;
}
